import * as z from "zod";

const OperationPathSchema = z.array(z.string().or(z.number()));

const ObjectSetOperationSchema = z.object({
  type: z.literal("object-set"),
  /** if an existing value exists at that path it is overwritten */
  path: OperationPathSchema,
  value: z.any().refine((val) => val !== undefined, {
    message: `value cannot be undefined`,
  }),
});

const ObjectDeleteOperationSchema = z.object({
  type: z.literal("object-delete"),
  path: OperationPathSchema,
});

/** does not support sparse arrays */
const ArrayInsertOperationSchema = z.object({
  type: z.literal("array-insert"),
  path: OperationPathSchema,
  /** if item exists at that index it is shifted to the right */
  index: z.number(),
  values: z.array(
    z.any().refine((val) => val !== undefined, {
      message: `value cannot be undefined`,
    }),
  ),
});

/** does not support sparse arrays */
const ArrayDeleteOperationSchema = z.object({
  type: z.literal("array-delete"),
  path: OperationPathSchema,
  /** inclusive */
  startIndex: z.number(),
  /** inclusive */
  endIndex: z.number(),
});

export const OperationSchema = z.discriminatedUnion("type", [
  ObjectSetOperationSchema,
  ObjectDeleteOperationSchema,
  ArrayInsertOperationSchema,
  ArrayDeleteOperationSchema,
]);
export type Operation = z.infer<typeof OperationSchema>;

const RevisionSchema = z.object({
  id: z.string().optional(),
  operations: z.array(OperationSchema),
  /** this version number can be optimistic */
  version: z.number().optional(),
  clientId: z.string().optional(),
  sessionId: z.string().optional(),
});
// revision persisted by the server, server will fill in or overwite all fields that were optimistic/missing
// on a client's queued local revision
const PersistedRevisionSchema = RevisionSchema.required();
export type Revision = z.infer<typeof RevisionSchema>;
export type PersistedRevision = z.infer<typeof PersistedRevisionSchema>;

export type TranscriptRevisions = Record<string, PersistedRevision>;

export type RevisionStatus =
  | { status: "in_progress" }
  | { status: "complete"; revision: PersistedRevision | null }
  | { status: "error"; reason?: string };

const TokenSelectionNodeSchema = z.object({
  type: z.literal("token"),
  sliceIndex: z.number(),
  tokenIndex: z.number(),
  textOffset: z.number(),
});
const TokenSpaceSelectionNodeSchema = z.object({
  type: z.literal("token-space"),
  sliceIndex: z.number(),
  tokenIndex: z.number(),
  textOffset: z.number(),
});

const TranscriptSelectionNodeSchema = z.discriminatedUnion("type", [
  TokenSelectionNodeSchema,
  TokenSpaceSelectionNodeSchema,
]);

export const MultiplayerServerMessageSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("authenticated"),
    payload: z.object({
      sessionId: z.string(),
    }),
  }),
  z.object({
    type: z.literal("presence-add"),
    payload: z.object({
      clientId: z.string(),
      sessionId: z.string(),
      clientName: z.string(),
    }),
  }),
  z.object({
    type: z.literal("presence-remove"),
    payload: z.object({
      sessionId: z.string(),
    }),
  }),
  z.object({
    type: z.literal("error"),
    payload: z.object({
      code: z.string(),
      title: z.string(),
      detail: z.string(),
    }),
  }),
  z.object({
    type: z.literal("revision"),
    payload: PersistedRevisionSchema,
  }),
  z.object({
    type: z.literal("ack-revision"),
    payload: z.object({
      id: z.string(),
      revision: PersistedRevisionSchema.nullable(),
    }),
  }),
  z.object({
    type: z.literal("cursor-update"),
    payload: z.object({
      start: TranscriptSelectionNodeSchema.nullable(),
      end: TranscriptSelectionNodeSchema.nullable(),
      sessionId: z.string(),
    }),
  }),
  z.object({
    type: z.literal("reconnect"),
    payload: z.object({
      detail: z.string(),
    }),
  }),
]);

export type MultiplayerServerMessage = z.infer<
  typeof MultiplayerServerMessageSchema
>;

export const MultiplayerClientMessageSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("authenticate"),
    payload: z.object({
      token: z.string(),
    }),
  }),
  z.object({
    type: z.literal("revision"),
    payload: z.object({
      id: PersistedRevisionSchema.shape.id,
      operations: PersistedRevisionSchema.shape.operations,
      version: PersistedRevisionSchema.shape.version,
    }),
  }),
  z.object({
    type: z.literal("cursor-update"),
    payload: z.object({
      start: TranscriptSelectionNodeSchema.nullable(),
      end: TranscriptSelectionNodeSchema.nullable(),
      sessionId: z.string(),
    }),
  }),
]);

export type MultiplayerClientMessage = z.infer<
  typeof MultiplayerClientMessageSchema
>;

export const HistoricalTranscriptSearchParamSchema = z.object({
  search: z.string(),
  sort: z.enum([
    "name",
    "started_on",
    "updated_on",
    "status",
    "media_type",
    "media_duration",
  ]),
  direction: z.enum(["ascending", "descending"]),
  page: z.string(),
});

export type HistoricalTranscriptSearchParams = z.infer<
  typeof HistoricalTranscriptSearchParamSchema
>;
